import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Box,
  Typography,
  Alert,
} from "@mui/material";
import contractItemsApi from "../../services/contractItems.service";
import { formatCurrency } from "../../../../utils/formatters";
import { AppButton } from "components/common/buttons";
import { AppAutocomplete, AppSelect, AppTextField } from "components/common/inputs";

const ContractItemModal = ({
  open,
  onClose,
  onSuccess,
  contractId,
  editMode = false,
  itemData = null,
  parentItem = null,
}) => {
  const [formData, setFormData] = useState({
    itemName: "",
    description: "",
    unit: "",
    contractQuantity: "",
    unitPrice: "",
    contractAmount: "", // Thêm contractAmount vào state
    startDate: "",
    endDate: "",
    status: "Chưa bắt đầu",
    priority: 2,
    completionPercentage: 0,
    difficultyLevel: 1,
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [generatedCode, setGeneratedCode] = useState("");
  const [errors, setErrors] = useState({});

  // Hàm format số theo chuẩn Việt Nam (dấu "." phân tách hàng nghìn, dấu "," phân tách thập phân)
  const formatNumber = (value) => {
    if (!value) return "";

    // Xóa tất cả ký tự không phải số, dấu "," và "."
    const cleanValue = value.toString().replace(/[^\d,.]/g, "");

    // Tách phần nguyên và phần thập phân
    const parts = cleanValue.split(",");
    const integerPart = parts[0].replace(/\./g, ""); // Xóa dấu "." cũ
    const decimalPart = parts[1] ? "," + parts[1] : "";

    // Format phần nguyên với dấu "." phân tách hàng nghìn
    const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ".");

    return formattedInteger + decimalPart;
  };

  // Hàm unformat số (chuyển về dạng số thuần túy)
  const unformatNumber = (value) => {
    if (!value) return "";
    return value.toString().replace(/\./g, "").replace(",", ".");
  };

  // Hàm xử lý thay đổi cho trường unitPrice
  const handleUnitPriceChange = (value) => {
    const formattedValue = formatNumber(value);

    setFormData((prev) => {
      const newFormData = { ...prev, unitPrice: formattedValue };

      // Auto-calculate contract amount when quantity or price changes
      const quantity = Number(prev.contractQuantity) || 0;
      const price = Number(unformatNumber(formattedValue)) || 0;
      newFormData.contractAmount = quantity && price ? quantity * price : 0;

      return newFormData;
    });

    // Clear error when user starts typing
    if (errors.unitPrice) {
      setErrors((prev) => ({ ...prev, unitPrice: "" }));
    }
  };

  // Predefined options
  const statusOptions = [
    "Chưa bắt đầu",
    "Đang thực hiện",
    "Hoàn thành",
    "Tạm dừng",
    "Hủy bỏ",
  ];

  const priorityOptions = [
    { value: 1, label: "Cao" },
    { value: 2, label: "Trung bình" },
    { value: 3, label: "Thấp" },
  ];

  const difficultyOptions = [
    { value: 1, label: "1" },
    { value: 2, label: "2" },
    { value: 3, label: "3" },
    { value: 4, label: "4" },
    { value: 5, label: "5" },
  ];

  const [unitOptions, setUnitOptions] = useState([
    "%",
    "Bộ",
    "Cái",
    "Điểm",
    "Gói",
    "ha",
    "kg",
    "m",
    "m²",
    "Mảnh QĐ",
    "Ngày",
    "Phần",
    "Tấn",
    "Tháng",
  ]);

  useEffect(() => {
    let active = true;
    const fetchUnits = async () => {
      try {
        const units = await contractItemsApi.getUnitsInContract();
        if (active && Array.isArray(units) && units.length > 0) {
          const names = units.map((u) => u.name || u.Name).filter(Boolean);
          if (names.length > 0) {
            setUnitOptions(names);
          }
        }
      } catch (err) {
        console.error("Error loading units in contract:", err);
      }
    };
    if (open) {
      fetchUnits();
    }
    return () => {
      active = false;
    };
  }, [open]);

  // Load form data when editing
  useEffect(() => {
    if (editMode && itemData) {
      setFormData({
        itemName: itemData.itemName || "",
        description: itemData.description || "",
        unit: itemData.unit || "",
        contractQuantity: itemData.contractQuantity || "",
        unitPrice: formatNumber(itemData.unitPrice || ""),
        contractAmount: itemData.contractAmount || "", // Thêm contractAmount
        startDate: itemData.startDate
          ? new Date(itemData.startDate).toISOString().split("T")[0]
          : "",
        endDate: itemData.endDate
          ? new Date(itemData.endDate).toISOString().split("T")[0]
          : "",
        status: itemData.status || "Chưa bắt đầu",
        priority: itemData.priority || 2,
        completionPercentage: itemData.completionPercentage || 0,
        difficultyLevel: itemData.difficultyLevel || 1,
        notes: itemData.notes || "",
      });
    } else {
      // Reset form for new item
      setFormData({
        itemName: "",
        description: "",
        unit: "",
        contractQuantity: "",
        unitPrice: "",
        contractAmount: "", // Thêm contractAmount
        startDate: "",
        endDate: "",
        status: "Chưa bắt đầu",
        priority: 2,
        completionPercentage: 0,
        difficultyLevel: 1,
        notes: "",
      });
    }
  }, [editMode, itemData]);

  // Generate item code when adding new item
  useEffect(() => {
    const generateItemCode = async () => {
      try {
        const response = await contractItemsApi.generateItemCode(
          contractId,
          parentItem?.itemId
        );
        if (response.Success) {
          setGeneratedCode(response.ItemCode);
        }
      } catch (error) {
        console.error("Error generating item code:", error);
      }
    };

    if (open && !editMode && contractId) {
      generateItemCode();
    }
  }, [open, editMode, contractId, parentItem]);

  const handleInputChange = (field, value) => {
    // Prevent editing completion percentage as it's auto-calculated from payments
    if (field === "completionPercentage") {
      console.warn(
        "CompletionPercentage is read-only and calculated from payments"
      );
      return;
    }

    // Xử lý riêng cho unitPrice để format số
    if (field === "unitPrice") {
      handleUnitPriceChange(value);
      return;
    }

    setFormData((prev) => {
      const newFormData = { ...prev, [field]: value };

      // Auto-calculate contract amount when quantity or price changes
      if (field === "contractQuantity") {
        const quantity = Number(value) || 0;
        const price = Number(unformatNumber(prev.unitPrice)) || 0;
        newFormData.contractAmount = quantity && price ? quantity * price : 0;
      }

      return newFormData;
    });

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: "" }));
    }
  };

  const handleDateChange = (name, dateStr) => {
    setFormData((prev) => {
      const nextFormData = { ...prev, [name]: dateStr };

      const startDate = name === "startDate" ? dateStr : nextFormData.startDate;
      const endDate = name === "endDate" ? dateStr : nextFormData.endDate;

      if (startDate && endDate) {
        if (new Date(startDate) > new Date(endDate)) {
          setErrors((prevErr) => ({
            ...prevErr,
            endDate: "Ngày kết thúc phải sau ngày bắt đầu",
          }));
        } else {
          setErrors((prevErr) => ({ ...prevErr, endDate: undefined }));
        }
      }

      return nextFormData;
    });
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const validateForm = () => {
    const newErrors = {};

    // Bỏ validation bắt buộc cho itemName vì đã cho phép null trong database
    // if (!formData.itemName.trim()) {
    //   newErrors.itemName = "Hạng mục công việc là bắt buộc";
    // }

    // Validate dates
    if (formData.startDate && formData.endDate) {
      if (new Date(formData.startDate) > new Date(formData.endDate)) {
        newErrors.endDate = "Ngày kết thúc phải sau ngày bắt đầu";
      }
    }

    // Validate numbers
    if (
      formData.contractQuantity &&
      isNaN(parseFloat(formData.contractQuantity))
    ) {
      newErrors.contractQuantity = "Khối lượng phải là số hợp lệ";
    }

    if (formData.unitPrice) {
      const unformattedPrice = unformatNumber(formData.unitPrice);
      if (
        isNaN(parseFloat(unformattedPrice)) ||
        parseFloat(unformattedPrice) < 0
      ) {
        newErrors.unitPrice = "Đơn giá phải là số dương hợp lệ";
      }
    }

    // Validate completion percentage - Removed because it's now auto-calculated from payments
    // Completion percentage is read-only and managed by payment system

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      // Debug logging để kiểm tra độ chính xác

      // Tính toán contractAmount từ contractQuantity và unitPrice - Sử dụng Number() thay vì parseFloat()
      const quantity = formData.contractQuantity
        ? Number(formData.contractQuantity)
        : null;
      const price = formData.unitPrice
        ? Number(unformatNumber(formData.unitPrice))
        : null;
      const contractAmount = quantity && price ? quantity * price : null;

      const submitData = {
        ...formData,
        contractId: parseInt(contractId),
        parentItemId: parentItem?.itemId || null,
        contractQuantity: quantity,
        unitPrice: price,
        contractAmount: contractAmount, // Thêm contractAmount được tính toán
        // completionPercentage: removed - now auto-calculated from payments
        difficultyLevel: formData.difficultyLevel
          ? parseInt(formData.difficultyLevel)
          : 1,
        startDate: formData.startDate || null,
        endDate: formData.endDate || null,
      };

      let response;
      if (editMode) {
        response = await contractItemsApi.updateItem(
          itemData.itemId,
          submitData
        );
      } else {
        response = await contractItemsApi.createItemWithAutoCode(submitData);
      }

      if (response.success || response.Success) {
        onSuccess?.(
          editMode ? "Cập nhật hạng mục thành công" : "Tạo hạng mục thành công"
        );
        handleClose();
      } else {
        setErrors({
          submit: response.message || response.Message || "Có lỗi xảy ra",
        });
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      setErrors({ submit: "Lỗi kết nối đến máy chủ" });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      itemName: "",
      description: "",
      unit: "",
      contractQuantity: "",
      unitPrice: "",
      contractAmount: "", // Thêm contractAmount
      startDate: "",
      endDate: "",
      status: "Chưa bắt đầu",
      priority: 2,
      completionPercentage: 0,
      difficultyLevel: 1,
      notes: "",
    });
    setErrors({});
    setGeneratedCode("");
    onClose();
  };

  const calculatePreviewAmount = () => {
    const quantity = parseFloat(formData.contractQuantity) || 0;
    const price = parseFloat(unformatNumber(formData.unitPrice)) || 0;
    return quantity * price;
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          border: "1px solid #e0e0e0",
          boxShadow: "none",
        },
      }}
    >
      <DialogTitle
        sx={{
          fontFamily: "Inter, sans-serif",
          fontSize: "1.25rem",
          fontWeight: 600,
          borderBottom: "1px solid #e0e0e0",
          py: 1
        }}
      >
        {editMode ? "Chỉnh sửa hạng mục" : "Thêm hạng mục mới"}
        {parentItem && (
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Inter, sans-serif",
              fontSize: "0.875rem",
              color: "text.secondary",
              mt: 0.5,
            }}
          >
            Hạng mục con của: {parentItem.itemCode} - {parentItem.itemName}
          </Typography>
        )}
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        {errors.submit && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: "Inter, sans-serif" }}
          >
            {errors.submit}
          </Alert>
        )}

        {/* STT (chỉ hiển thị khi thêm mới) */}
        {!editMode && generatedCode && (
          <Box
            mb={2}
            p={2}
            sx={{ backgroundColor: "#f5f5f5", borderRadius: 1 }}
          >
            <Typography
              variant="body2"
              sx={{
                fontFamily: "Inter, sans-serif",
                fontSize: "0.875rem",
                fontWeight: 500,
              }}
            >
              STT sẽ được tạo: <strong>{generatedCode}</strong>
            </Typography>
          </Box>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          {/* Hạng mục công việc - 12/12 */}
          <Grid size={12}>
            <AppTextField
              fullWidth
              label="Hạng mục công việc"
              value={formData.itemName}
              onChange={(value) => handleInputChange("itemName", value)}
              error={!!errors.itemName}
              helperText={errors.itemName}
            />
          </Grid>

          {/* ĐVT - 6/12 */}
          <Grid size={6}>
            <AppAutocomplete
              label="ĐVT"
              options={unitOptions}
              value={formData.unit}
              onChange={(value) => handleInputChange("unit", value)}
              error={!!errors.unit}
              helperText={errors.unit}
            />
          </Grid>

          {/* Khối lượng và Đơn giá - 6/12 mỗi cái */}
          <Grid size={6}>
            <AppTextField
              fullWidth
              label="Khối lượng"
              type="number"
              value={formData.contractQuantity}
              onChange={(value) =>
                handleInputChange("contractQuantity", value)
              }
              error={!!errors.contractQuantity}
              helperText={errors.contractQuantity}
              inputProps={{ min: 0, step: 0.0001 }}
            />
          </Grid>

          <Grid size={6}>
            <AppTextField
              fullWidth
              label="Đơn giá (VND)"
              value={formData.unitPrice}
              onChange={(value) => handleInputChange("unitPrice", value)}
              error={!!errors.unitPrice}
              helperText={errors.unitPrice}
              placeholder="Nhập đơn giá (VD: 1.000.000,50)"
            />
          </Grid>

          {/* Thành tiền preview */}
          {formData.contractQuantity && formData.unitPrice && (
            <Grid size={12}>
              <Box p={2} sx={{ backgroundColor: "#e3f2fd", borderRadius: 1 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontFamily: "Inter, sans-serif",
                    fontSize: "0.875rem",
                    fontWeight: 500,
                  }}
                >
                  Thành tiền dự kiến:{" "}
                  {formatCurrency(calculatePreviewAmount()).replace(
                    /\s*₫\s*$/,
                    ""
                  )}
                </Typography>
              </Box>
            </Grid>
          )}

          {/* Ngày bắt đầu và kết thúc - 6/12 mỗi cái */}
          <Grid size={6}>
            <AppTextField
              fullWidth
              label="Ngày bắt đầu"
              type="date"
              value={formData.startDate}
              onChange={(dateStr) => handleDateChange("startDate", dateStr)}
              error={!!errors.startDate}
              helperText={errors.startDate}
            />
          </Grid>

          <Grid size={6}>
            <AppTextField
              fullWidth
              label="Ngày kết thúc"
              type="date"
              value={formData.endDate}
              onChange={(dateStr) => handleDateChange("endDate", dateStr)}
              error={!!errors.endDate}
              helperText={errors.endDate}
            />
          </Grid>

          {/* Trạng thái, Độ ưu tiên, Khó khăn - 6/12 mỗi cái */}
          <Grid size={6}>
            <AppSelect
              fullWidth
              label="Trạng thái"
              name="status"
              value={formData.status}
              options={statusOptions.map((opt) => ({ value: opt, label: opt }))}
              onChange={(value) => handleInputChange("status", value)}
              error={!!errors.status}
              helperText={errors.status}
            />
          </Grid>

          <Grid size={6}>
            <AppSelect
              fullWidth
              label="Độ ưu tiên"
              name="priority"
              value={formData.priority}
              options={priorityOptions}
              onChange={(value) => handleInputChange("priority", value)}
              error={!!errors.priority}
              helperText={errors.priority}
            />
          </Grid>

          <Grid size={6}>
            <AppSelect
              fullWidth
              label="Khó khăn"
              name="difficultyLevel"
              value={formData.difficultyLevel}
              options={difficultyOptions}
              onChange={(value) => handleInputChange("difficultyLevel", value)}
              error={!!errors.difficultyLevel}
              helperText={errors.difficultyLevel}
            />
          </Grid>

          {/* Tiến độ - 6/12 (đã bỏ Mô tả để tiết kiệm không gian) */}
          <Grid size={6}>
            <AppTextField
              fullWidth
              label="Tiến độ (%)"
              type="number"
              value={formData.completionPercentage}
              disabled
              helperText="Tiến độ được tự động tính từ khối lượng thanh toán"
              inputProps={{ min: 0, max: 100, step: 0.1 }}
            />
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ px: 3, borderTop: "1px solid #e0e0e0" }}>
        <AppButton variant="outlined" onClick={handleClose} >
          Hủy
        </AppButton>
        <AppButton
          onClick={handleSubmit}
          disabled={loading}
          variant="contained"
        >
          {loading ? "Đang xử lý..." : editMode ? "Cập nhật" : "Tạo hạng mục"}
        </AppButton>
      </DialogActions>
    </Dialog>
  );
};

export default ContractItemModal;
